/**
 * Carbon footprint readout for the designed column.
 *
 * The code check returns an embodied-carbon result per element in
 * `selProject.data.data.output.carbon` (ng.environmental_parameters.ElementCarbonFootprint) — the
 * A1-A3 product-stage GWP, split per material. See Design/env_param.proto.
 *
 * It is shown as a chip floating over the viewport rather than as a section of the results panel,
 * so the number is on screen whether or not the results panel is open: carbon is a design driver,
 * and it only informs the section / grade / reinforcement choices if the user can see it while
 * making them. Clicking the chip expands the per-material breakdown in place.
 *
 * The figure does not wait for a code check: changing the section, the material, the column length
 * or (for concrete) the reinforcement resets it and recalculates against the /carbonfootprint
 * endpoint — see resetCarbonFootprint + updateCarbonFootprint, driven by the sync middleware, whose
 * CARBON_LIVE_UPDATE_MATERIALS decides which materials the live update exists for. That is all three
 * of them (RC-Design computes it for concrete, FEM-Design for steel and timber). Reset comes first,
 * so the readout is empty (`carbonStatus === "loading"`) while the new number is on its way rather
 * than showing the old one as if it still applied.
 *
 * The rendering itself is SXCarbonFootprintView, which needs nothing but React; this wrapper only
 * supplies what sx-column has and sx-beam does not — i18next translations and the sx-icons chevrons —
 * and the element weight. The carbon logic it shares with every app is helpers/carbonFootprint.ts.
 *
 * The chip is always mounted, also when there is no result — before the first calculation, between a
 * reset and its result, when a recalculation fails, and when a calculation ran but returned no
 * carbon (a design service that does not compute it, or missing emission data). An empty readout
 * says "this column has a carbon figure, it just is not calculated right now", which a widget that
 * disappears does not.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon, ChevronUpIcon } from "../../../external/sx-icons/src/icons";
import { ng } from "../../ngds";
import { structureMass } from "../../helpers/elementMass";
import { SXCarbonFootprintView } from "./SXCarbonFootprintView";

interface SXCarbonFootprintProps {
  /**
   * Distance from the right edge of the window, in px. Passed in from App so the chip stays
   * aligned with the viewport area as the results panel opens and closes — the same thing the
   * bottom toolbar does with its inline `right`.
   */
  right: number;
  project: any;
  carbonStatus: string;
}

export const SXCarbonFootprint = ({ right, project, carbonStatus }: SXCarbonFootprintProps) => {
  const { t } = useTranslation();
  const selProject = project;

  const carbon = selProject?.data?.data?.output?.carbon ?? [];

  // The weight, by the same rule the printout uses (elementMass): the self-weight the analysis is
  // given, integrated, summed over the elements. Shown beside the carbon rather than in its table,
  // since those rows are quantities in the unit each emission factor is declared per (m³ for
  // concrete and timber).
  const input = selProject?.data?.data?.input;
  const weightKg = React.useMemo(() => (input ? structureMass(ng.input.Data.fromObject(input)) : 0), [input]);

  const translate = React.useCallback(
    (key: string, fallback?: string) => (fallback === undefined ? t(`${key}.label`) : t(`${key}.label`, fallback)),
    [t],
  );

  return (
    <SXCarbonFootprintView
      carbon={carbon}
      status={carbonStatus}
      translate={translate}
      weightKg={weightKg}
      style={{ right }}
      expandIcon={<ChevronDownIcon size={12} />}
      collapseIcon={<ChevronUpIcon size={12} />}
    />
  );
};

export default SXCarbonFootprint;
